import React from 'react';
import PropTypes from 'prop-types';
import styles from './Table.module.scss';
import DropDownButton from '../dropDownButton/DropDownButton';

const Table = ({
  columns,
  data,
  onRowSelect,
  selectedRows = [],
  getActionItems,
  pagination,
  onPageChange,
  sortBy,
  sortOrder,
  onSort,
  emptyMessage = 'No data available.',
}) => {
  const handleSort = (sortKey) => {
    if (!onSort) return;
    const newOrder = sortBy === sortKey && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    onSort(sortKey, newOrder);
  };
  const handleSelectAll = (e) => {
    if (onRowSelect) {
      if (e.target.checked) {
        onRowSelect(data.map(row => row.id));
      } else {
        onRowSelect([]);
      }
    }
  };

  const handleRowSelect = (id) => {
    if (onRowSelect) {
      if (selectedRows.includes(id)) {
        onRowSelect(selectedRows.filter(rowId => rowId !== id));
      } else {
        onRowSelect([...selectedRows, id]);
      }
    }
  };

  return (
    <div className={styles.tableContainer}>
      <table>
        <thead>
          <tr>
            {onRowSelect && (
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={data.length > 0 && selectedRows.length === data.length}
                  onChange={handleSelectAll}
                />
              </th>
            )}
            {columns.map((col, index) => (
              <th
                key={index}
                className={col.sortKey ? styles.sortable : ''}
                onClick={() => col.sortKey && handleSort(col.sortKey)}
              >
                {col.header}
                {col.sortKey && (() => {
                  const isActive = sortBy === col.sortKey;
                  const activeColor = '#172B4D';
                  const inactiveColor = '#D0D0D0';
                  return (
                    <span className={styles.sortIcons}>
                      <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.03556 0.146447C3.8403 -0.0488155 3.52372 -0.0488156 3.32845 0.146447L0.146473 3.32843C-0.0487894 3.52369 -0.0487894 3.84027 0.146473 4.03553C0.341735 4.2308 0.658317 4.2308 0.85358 4.03553L3.68201 1.20711L6.51043 4.03553C6.7057 4.2308 7.02228 4.2308 7.21754 4.03553C7.4128 3.84027 7.4128 3.52369 7.21754 3.32843L4.03556 0.146447ZM3.18201 9.5C3.18201 9.77614 3.40586 10 3.68201 10C3.95815 10 4.18201 9.77614 4.18201 9.5L3.68201 9.5L3.18201 9.5ZM3.68201 0.5L3.18201 0.5L3.18201 9.5L3.68201 9.5L4.18201 9.5L4.18201 0.5L3.68201 0.5Z" fill={isActive && sortOrder === 'ASC' ? activeColor : inactiveColor}/>
                        <path d="M12.182 0.5C12.182 0.223858 11.9581 -1.20706e-08 11.682 0C11.4059 1.20706e-08 11.182 0.223858 11.182 0.5L11.682 0.5L12.182 0.5ZM11.3285 9.85355C11.5237 10.0488 11.8403 10.0488 12.0356 9.85355L15.2175 6.67157C15.4128 6.47631 15.4128 6.15973 15.2175 5.96447C15.0223 5.7692 14.7057 5.7692 14.5104 5.96447L11.682 8.79289L8.85358 5.96447C8.65832 5.7692 8.34174 5.7692 8.14647 5.96447C7.95121 6.15973 7.95121 6.47631 8.14647 6.67157L11.3285 9.85355ZM11.682 0.5L11.182 0.5L11.182 9.5L11.682 9.5L12.182 9.5L12.182 0.5L11.682 0.5Z" fill={isActive && sortOrder === 'DESC' ? activeColor : inactiveColor}/>
                      </svg>
                    </span>
                  );
                })()}
              </th>
            ))}
            {getActionItems && <th></th>}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (onRowSelect ? 1 : 0) + (getActionItems ? 1 : 0)}
                className={styles.noData}
              >
                {emptyMessage}
              </td>
            </tr>
          )}
          {data.map((row, rowIndex) => (
            <tr key={row.id || rowIndex}>
              {onRowSelect && (
                <td className={styles.checkboxCell}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={selectedRows.includes(row.id)}
                    onChange={() => handleRowSelect(row.id)}
                  />
                </td>
              )}
              {columns.map((col, colIndex) => (
                <td key={colIndex}>
                  {col.render ? col.render(row) : row[col.key]}
                </td>
              ))}
              {getActionItems && (
                <td>
                  <DropDownButton
                    type="dots"
                    usePortal={true}
                    items={getActionItems(row)}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {pagination && (
        <div className={styles.pagination}>
          <div className={styles.pageInfo}>
            {/* Mocked pagination text */}
            {pagination.start}-{pagination.end} of {pagination.total}
          </div>
          <div className={styles.controls}>
             <button 
               disabled={!pagination.hasPrev}
               onClick={() => onPageChange && onPageChange(pagination.current - 1)}
             >
               Previous
             </button>
             <button
               disabled={!pagination.hasNext}
               onClick={() => onPageChange && onPageChange(pagination.current + 1)}
             >
               Next
             </button>
          </div>
        </div>
      )}
    </div>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string.isRequired,
    key: PropTypes.string,
    render: PropTypes.func,
    textKey: PropTypes.string // For complex cells like avatar + text
  })).isRequired,
  data: PropTypes.array.isRequired,
  onRowSelect: PropTypes.func,
  selectedRows: PropTypes.array,
  getActionItems: PropTypes.func,
  pagination: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    start: PropTypes.number,
    end: PropTypes.number,
    hasPrev: PropTypes.bool,
    hasNext: PropTypes.bool
  }),
  onPageChange: PropTypes.func,
  sortBy: PropTypes.string,
  sortOrder: PropTypes.oneOf(['ASC', 'DESC']),
  onSort: PropTypes.func,
  emptyMessage: PropTypes.string,
};

export default Table;
